import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { ReadinessLevel, ReadinessTier } from "@/lib/api/work-items";

/**
 * Grooming tiers gate Epic Request conversion, so they get solid status
 * colours. Sprint readiness is report-only (D11) and never blocks anything,
 * so it is drawn as an outlined, quieter badge with the same hue.
 */
const readinessBadgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap",
  {
    variants: {
      level: {
        grooming: "",
        sprint: "bg-transparent",
      },
      tier: {
        pass: "",
        advisory: "",
        "critical-fail": "",
        unsupported: "",
      },
    },
    compoundVariants: [
      { level: "grooming", tier: "pass", className: "border-transparent bg-green-900/30 text-green-400" },
      { level: "grooming", tier: "advisory", className: "border-transparent bg-yellow-900/30 text-yellow-400" },
      { level: "grooming", tier: "critical-fail", className: "border-transparent bg-red-900/30 text-red-400" },
      { level: "grooming", tier: "unsupported", className: "border-transparent bg-white/[0.06] text-muted-foreground" },
      { level: "sprint", tier: "pass", className: "border-green-500/30 text-green-400/80" },
      { level: "sprint", tier: "advisory", className: "border-yellow-500/30 text-yellow-400/80" },
      { level: "sprint", tier: "critical-fail", className: "border-red-500/30 text-red-400/80" },
      { level: "sprint", tier: "unsupported", className: "border-white/[0.08] text-muted-foreground" },
    ],
  }
);

const LEVEL_LABELS: Record<ReadinessLevel, string> = {
  grooming: "Grooming",
  sprint: "Sprint",
};

const TIER_LABELS: Record<ReadinessTier, string> = {
  pass: "Ready",
  advisory: "Advisory",
  "critical-fail": "Not ready",
  unsupported: "Not scored",
};

const LEVEL_TITLES: Record<ReadinessLevel, string> = {
  grooming: "Ready for grooming: the bar an item must meet before it can become an Epic Request.",
  sprint: "Ready for sprint: grooming plus an estimate. Report-only; it never blocks anything.",
};

export interface ReadinessBadgeProps {
  level: ReadinessLevel;
  tier: ReadinessTier;
  className?: string;
}

/** One readiness level of an ADO item, e.g. "Grooming: Not ready". */
export function ReadinessBadge({ level, tier, className }: ReadinessBadgeProps) {
  return (
    <span
      className={cn(readinessBadgeVariants({ level, tier }), className)}
      title={LEVEL_TITLES[level]}
      data-level={level}
      data-tier={tier}
    >
      <span className="opacity-70">{LEVEL_LABELS[level]}:</span>
      {TIER_LABELS[tier]}
    </span>
  );
}
